import { MagicVariables } from '@/types';
import { scrollIntoView } from '@/utils/general';
import { SEARCH_RESULTS_CONTENT_SELECTOR } from './config';

export interface ISelectionHandler {
    refresh: () => void;
    selectPrevious: () => void;
    selectNext: () => void;
    getSelectedItemText: () => string;
    getActiveElement: () => HTMLAnchorElement | undefined;
    onTabChange: () => void;
    resetSelection: () => void;
    setRootElement: (element: MagicVariables['$root']) => void;
}

export class SelectionHandler implements ISelectionHandler, MagicVariables {
    $root?: MagicVariables['$root'];

    searchResults: HTMLAnchorElement[] = [];

    /**
     * Index of the currently selected item in the search results.
     * -1 means no item is selected.
     * 0 means the first item is selected.
     * 1 means the second item is selected.
     * and so on...
     */
    selected = -1;

    refresh() {
        this.searchResults = this.getSearchResults();
        this.setSelectedItem();
    }

    resetSelection() {
        this.removeSelectionAttribute();
        this.selected = -1;
    }

    onTabChange() {
        this.refresh();
        this.resetSelection();
    }

    selectPrevious() {
        this.removeSelectionAttribute();

        if (this.searchResults.length < 1) {
            return;
        }

        if (this.selected > 0) {
            this.selected--;
        } else {
            this.selected = this.searchResults.length - 1;
        }

        this.setSelectedItem();
    }

    selectNext() {
        this.removeSelectionAttribute();

        if (this.searchResults.length < 1) {
            return;
        }

        if (this.selected < this.searchResults.length - 1) {
            this.selected++;
        } else {
            this.selected = 0;
        }

        this.setSelectedItem();
    }

    setSelectedItem() {
        if (this.selected < 0) {
            return;
        }

        const activeElement = this.getActiveElement();

        // Selection can't be made at the same index where it was before, so we reset it
        if (!activeElement) {
            this.resetSelection();
            return;
        }

        // setting data-selected attribute
        activeElement.dataset.selected = 'true';

        scrollIntoView(activeElement);
    }

    getSelectedItemText() {
        const activeElement = this.getActiveElement();
        return activeElement?.innerText.trim() || '';
    }

    removeSelectionAttribute() {
        const activeElement = this.getActiveElement();

        if (!activeElement?.dataset.selected) {
            return;
        }

        delete activeElement.dataset.selected;
    }

    getSearchResults() {
        return [...this.$root.querySelectorAll(SEARCH_RESULTS_CONTENT_SELECTOR)] as HTMLAnchorElement[];
    }

    getActiveElement(): HTMLAnchorElement | undefined {
        return this.searchResults[this.selected];
    }

    setRootElement(element: MagicVariables['$root']) {
        this.$root = element;
    }
}
